import SvgIcon from '@/shared/ui/svg-icon'
import { Tooltip as AntTooltip, type ButtonProps } from 'antd'
import type { SizeType } from 'antd/es/config-provider/SizeContext'
import type { CSSProperties, ReactNode } from 'react'
import UiButton from '../ui-button'
import './buttonCircle.scss'

/**
 * Ý nghĩa hành động, không phải tên màu — chỗ dùng khai "việc này là gì", bảng dưới
 * quyết định màu. Đổi tông của cả app thì sửa một chỗ này.
 */
export type UiButtonCircleTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger'

const TONE_COLOR: Record<UiButtonCircleTone, ButtonProps['color']> = {
  neutral: 'default', // xem, tải, mở chi tiết — không đổi dữ liệu
  primary: 'primary', // hành động chính: sửa, ký nháy, gửi
  success: 'green', // xác nhận, duyệt, ký thẩm quyền
  warning: 'orange', // cần cân nhắc: tạm dừng, gỡ
  danger: 'danger', // không đảo ngược: xoá, thu hồi, trả lại
}

interface UiButtonCircleProps extends ButtonProps {
  iconName?: string
  style?: CSSProperties
  children?: ReactNode
  icon?: ReactNode
  size?: SizeType
  hidden?: boolean
  title?: string
  /** Ý nghĩa hành động — quyết định màu. Mặc định `neutral`. */
  tone?: UiButtonCircleTone
  /**
   * Ghi đè màu mà `tone` suy ra. Chỉ nhận **màu ngữ nghĩa của antd**
   * (`primary` · `danger` · `green` · `orange` · `default`), không nhận mã hex —
   * hex trong `src/features/**` bị `ds-guard` chặn.
   *
   * Ưu tiên: `color` > `tone` > `danger` > `neutral`.
   *
   * Mặc định **đừng dùng**: khai `tone` theo ý nghĩa hành động để đổi tông cả app
   * chỉ ở một chỗ. Chỉ dùng `color` khi một nút thật sự lệch khỏi 5 ý nghĩa có sẵn.
   */
  color?: ButtonProps['color']
  /**
   * Mặc định nút thao tác trong bảng **không viền** (icon trần, hover mới hiện nền nhạt).
   * Bật `bordered` cho chỗ cần nút viền tròn như cũ (vd icon trên header).
   */
  bordered?: boolean
  /** Điểm bám cho e2e. Không truyền thì không render attribute nào. */
  testId?: string
}

export default function UiButtonCircle(props: UiButtonCircleProps) {
  const {
    children,
    style = {},
    iconName,
    icon,
    size = 'small',
    hidden = false,
    title = undefined,
    testId,
    bordered = false,
    danger = false,
    tone,
    color: colorOverride,
    ...rest
  } = props
  // `danger` cũ vẫn chạy: coi như tone `danger`. `color` truyền tay thắng tất cả.
  const color = colorOverride ?? TONE_COLOR[tone ?? (danger ? 'danger' : 'neutral')]
  if (hidden) return null
  return (
    <AntTooltip title={title || children}>
      <UiButton
        shape="circle"
        /**
         * KHÔNG dùng `ghost`: nó bật `ant-btn-background-ghost` — kiểu dành cho nền tối,
         * ép chữ và viền thành trắng, nên trên nền bảng trắng thì nút tàng hình.
         *
         * `danger` phải đi qua `color`: antd 6 cho `color` thắng `danger`, nên cách cũ
         * (`color="default"` + `danger`) chỉ thêm class `ant-btn-dangerous` mà mất màu đỏ.
         */
        variant={bordered ? 'outlined' : 'text'}
        color={color}
        data-testid={testId}
        style={{
          backgroundColor: 'transparent',
          display: 'flex !important',
          alignItems: 'center !important',
          justifyContent: 'center !important',
          padding: 0,
          ...style,
        }}
        className="btn-circle-custom"
        size={size}
        {...rest}
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {icon || (iconName && <SvgIcon name={iconName} />)}
        </span>
      </UiButton>
    </AntTooltip>
  )
}
